import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from './ui/table';
import type { Follower } from '../types/user';
import { followersService, type FollowableKind } from '../services/followers';

interface FollowersListProps {
  /** The preview the profile came with — the newest few, not everybody. */
  followers: Follower[];
  /**
   * How many followers there really are. The preview is capped by the API, so
   * "and N others" must come from here, not from the preview's length.
   */
  totalCount?: number;
  /** Where the full list is fetched from when the dialog opens. */
  followable?: { kind: FollowableKind; slug: string };
  maxVisible?: number;
}

export default function FollowersList({
  followers,
  totalCount,
  followable,
  maxVisible = 2,
}: FollowersListProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = useState(false);
  // The dialog's own copy of the list: null until the first page arrives, so
  // the preview stands in meanwhile and the dialog never opens empty.
  const [fetched, setFetched] = useState<Follower[] | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const total = totalCount ?? followers.length;

  const followableKind = followable?.kind;
  const followableSlug = followable?.slug;

  // A new profile means a new list; forget the one fetched for the last.
  useEffect(() => {
    setFetched(null);
    setPage(1);
    setHasMore(false);
  }, [followableKind, followableSlug]);

  useEffect(() => {
    if (!dialogOpen || fetched !== null || !followableKind || !followableSlug) return;

    let cancelled = false;
    setLoading(true);
    followersService
      .list(followableKind, followableSlug, 1)
      .then((result) => {
        if (cancelled) return;
        setFetched(result.followers);
        setHasMore(result.hasMore);
        setPage(1);
      })
      .catch(() => {
        // The preview is still on screen; nothing more to say.
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dialogOpen, fetched, followableKind, followableSlug]);

  const loadMore = async () => {
    if (!followableKind || !followableSlug || loading) return;
    setLoading(true);
    try {
      const nextPage = page + 1;
      const result = await followersService.list(followableKind, followableSlug, nextPage);
      setFetched((current) => [...(current ?? []), ...result.followers]);
      setHasMore(result.hasMore);
      setPage(nextPage);
    } catch {
      // Leave what is already listed; the button stays for another try.
    } finally {
      setLoading(false);
    }
  };

  if (total === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        <span className="font-medium">{t('followers.followedBy')}:</span>{' '}
        {t('followers.noFollowers')}
      </p>
    );
  }

  const visibleFollowers = followers.slice(0, maxVisible);
  const remainingCount = total - visibleFollowers.length;
  const dialogFollowers = fetched ?? followers;

  const handleUserClick = (slug: string) => {
    setDialogOpen(false);
    navigate(`/users/${slug}`);
  };

  const handleOthersClick = () => {
    setDialogOpen(true);
  };

  return (
    <>
      <p className="text-sm text-muted-foreground">
        <span className="font-medium">{t('followers.followedBy')}:</span>{' '}
        {visibleFollowers.map((follower, index) => (
          <span key={follower.slug}>
            <button
              type="button"
              onClick={() => handleUserClick(follower.slug)}
              className="text-foreground hover:underline focus:outline-none"
            >
              {follower.username}
            </button>
            {index < visibleFollowers.length - 1 && ', '}
          </span>
        ))}
        {remainingCount > 0 && (
          <>
            {' '}{t('followers.and')}{' '}
            <button
              type="button"
              onClick={handleOthersClick}
              className="text-foreground hover:underline focus:outline-none font-medium"
            >
              {t('followers.xOthers', { count: remainingCount })}
            </button>
          </>
        )}
      </p>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('followers.dialogTitle')}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            <Table>
              <TableBody>
                {dialogFollowers.map((follower) => (
                  <TableRow
                    key={follower.slug}
                    className="cursor-pointer"
                    onClick={() => handleUserClick(follower.slug)}
                  >
                    <TableCell className="py-3">
                      <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10">
                          <AvatarImage
                            src={follower.avatar_small_url}
                            alt={follower.username}
                          />
                          <AvatarFallback>
                            {follower.username?.charAt(0)?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">{follower.username}</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {loading && (
              <div className="flex justify-center py-3" aria-label={t('followers.loading')}>
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
            {!loading && hasMore && (
              <div className="flex justify-center py-2">
                <Button variant="ghost" size="sm" onClick={loadMore}>
                  {t('followers.loadMore')}
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
